import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../utils/api";

const ThemeContext = createContext({ settings: null, reload: () => {} });

/** Convert a hex color to HSL so we can derive hover/light/border variants. */
function hexToHsl(hex) {
  const h = hex.replace("#", "");
  const bigint = parseInt(h.length === 3 ? h.split("").map(c => c + c).join("") : h, 16);
  const r = ((bigint >> 16) & 255) / 255;
  const g = ((bigint >> 8) & 255) / 255;
  const b = (bigint & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let hh = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hh = (g - b) / d + (g < b ? 6 : 0); break;
      case g: hh = (b - r) / d + 2; break;
      case b: hh = (r - g) / d + 4; break;
      default: break;
    }
    hh /= 6;
  }
  return { h: hh * 360, s: s * 100, l: l * 100 };
}

function hsl(h, s, l) { return `hsl(${h.toFixed(1)}, ${s.toFixed(1)}%, ${l.toFixed(1)}%)`; }

function applyTheme(primaryHex) {
  try {
    const { h, s, l } = hexToHsl(primaryHex || "#5C7A5E");
    const root = document.documentElement;
    root.style.setProperty("--sage", primaryHex);
    root.style.setProperty("--sage-hover", hsl(h, Math.min(100, s + 3), Math.max(8, l - 12)));
    root.style.setProperty("--sage-light", hsl(h, Math.max(10, s - 30), Math.min(96, l + 40)));
    root.style.setProperty("--sage-border", hsl(h, Math.max(10, s - 5), Math.max(35, Math.min(70, l + 8))));
    // Shadcn --primary uses space-separated HSL tokens (no hsl())
    root.style.setProperty("--primary", `${h.toFixed(1)} ${s.toFixed(1)}% ${l.toFixed(1)}%`);
  } catch (e) {
    // swallow — keep defaults
  }
}

export function ThemeProvider({ children }) {
  const [settings, setSettings] = useState(null);

  const reload = async () => {
    try {
      const r = await api.get("/public/settings");
      setSettings(r.data);
      if (r.data?.theme?.primary_color) applyTheme(r.data.theme.primary_color);
    } catch (e) {
      // public settings should never fail; fall back to defaults
      setSettings({ theme: { primary_color: "#5C7A5E" }, app_name: "ReconX" });
    }
  };

  useEffect(() => { reload(); }, []);

  return (
    <ThemeContext.Provider value={{ settings, reload }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() { return useContext(ThemeContext); }
